import { z } from 'zod';

/**
 * Ansichtseinstellungen werden pro Bereich (`scope`) als ein JSON-Dokument
 * gespeichert. Ein Scope entspricht einem `UiSettings`-Knoten im Backend; die
 * Liste hier muss zur Allowlist im Backend passen
 * (`domains/user-config/user-config.schema.ts`).
 */
export const SETTINGS_SCOPES = ['pinboard', 'teachingBlock', 'substitutions', 'solver'] as const;

export type SettingsScope = (typeof SETTINGS_SCOPES)[number];

/**
 * Jedes Feld ist optional mit Default und faellt per `.catch()` auf den Default
 * zurueck. Damit macht ein alter, umbenannter oder kaputter Wert nie die Seite
 * kaputt — er wird beim naechsten Speichern still durch den Default ersetzt.
 */
const bool = (fallback: boolean) => z.boolean().catch(fallback).default(fallback);
const px = (fallback: number, min: number, max: number) =>
  z.number().min(min).max(max).catch(fallback).default(fallback);
const percent = (fallback: number) => px(fallback, 0, 100);
const enumOf = <T extends string>(values: readonly [T, ...T[]], fallback: NoInfer<T>) =>
  z
    .enum(values as unknown as [T, ...T[]])
    .catch(fallback)
    .default(fallback);

/**
 * Wie {@link enumOf}, aber **ohne** Default: Das Feld bleibt `undefined`, solange
 * nichts gespeichert wurde. Fuer Werte, bei denen „noch nie gewaehlt" etwas anderes
 * bedeutet als „steht auf dem Standard".
 */
const optionalEnumOf = <T extends string>(values: readonly [T, ...T[]]) =>
  z
    .enum(values as unknown as [T, ...T[]])
    .optional()
    .catch(undefined);

/** Werte aus `GridType`/`GridContext`, `PreviewMapContext` und `BoardLayout`. */
const gridContextSchema = enumOf(['classes', 'rooms', 'subjects', 'teachers'], 'classes');

/**
 * Der zuletzt bewusst gewaehlte Board-Kontext. Bewusst ohne Default: Die
 * Kartenlogik setzt den Kontext beim Laden selbst, und nur ein tatsaechlich
 * gespeicherter Wert darf das ueberschreiben (siehe `restorePersistedContext`).
 */
const selectedContextSchema = optionalEnumOf(['classes', 'rooms', 'subjects', 'teachers']);
const previewMapContextSchema = enumOf(['classes', 'teachers', 'rooms'], 'classes');
const boardLayoutSchema = enumOf(['stacked', 'split'], 'stacked');

/**
 * Einstellungen, die Stundenplan- und Epochen-Board teilen. Die Defaults der
 * beiden Boards unterscheiden sich stellenweise (z. B. `boardTopHeight`), deshalb
 * gibt es zwei getrennte Scopes, die dieses Schema per `.extend()` anpassen.
 */
const sharedBoardSettingsSchema = z.object({
  rowHeight: px(32, 16, 200),
  groupRoomsByBuilding: bool(true),
  showTeacherInsteadOfSubject: bool(false),
  cardColorBySubject: bool(false),

  boardLayout: boardLayoutSchema,
  boardTopHeight: px(200, 0, 2000),
  boardSplitWidth: percent(34),
  boardPickerWidth: percent(70),
  boardPickerHeight: percent(60),

  showPreviewMap: bool(false),
  previewMapContext: previewMapContextSchema,
  previewMapAuto: bool(true),
  boardPreviewMapWidth: percent(30),
  boardPreviewMapHeight: percent(30),
});

const highlightModeSchema = <T extends string>(values: readonly [T, ...T[]], fallback: NoInfer<T>[]) =>
  z
    .array(z.enum(values as unknown as [T, ...T[]]))
    .catch(fallback)
    .default(fallback);

/** Stundenplan-Board (`PinboardUIStore`). */
export const pinboardSettingsSchema = sharedBoardSettingsSchema.extend({
  filterFunctionClasses: bool(false),

  showCardInfo: bool(true),
  showTimetablePreview: bool(false),
  boardTimetablePreviewTeachersWidth: percent(25),
  boardTimetablePreviewTeachersHeight: percent(25),
  boardTimetablePreviewRoomsWidth: percent(25),
  boardTimetablePreviewRoomsHeight: percent(25),
  timetablePreviewCellMinHeight: px(20, 8, 200),

  showEpochRanges: bool(true),
  showPause: bool(false),
  // Werte aus `HighlightType`.
  highlightMode: highlightModeSchema(['warnings', 'rooms', 'noIcons', 'conflicts'], ['warnings']),

  /** Zuletzt bewusst gewaehlter Board-Kontext (siehe `selectContext`). */
  context: selectedContextSchema,
});

export type PinboardSettings = z.infer<typeof pinboardSettingsSchema>;

/** Epochen-Board (`TeachingBlockUIStore`). */
export const teachingBlockSettingsSchema = sharedBoardSettingsSchema.extend({
  // Angelehnt an die Design-Hoehe der CardInfo, damit im gestapelten Layout
  // nichts vom Auswahl-Bereich abgeschnitten wird.
  boardTopHeight: px(280, 0, 2000),
  // Das Epochen-Board zeigt die Vorschau auch fuer Faecher.
  previewMapContext: gridContextSchema,
  // Werte aus `GridHighlightType`.
  highlightMode: highlightModeSchema(['warnings', 'rooms', 'noIcons', 'lengthDeviation'], ['warnings']),
  /** Zuletzt bewusst gewaehlter Board-Kontext (siehe `selectContext`). */
  gridContext: selectedContextSchema,
});

export type TeachingBlockSettings = z.infer<typeof teachingBlockSettingsSchema>;

/** Vertretungsplan. */
export const substitutionSettingsSchema = z.object({
  /** Faerbt die Eintraege im Stundenplan nach Fach oder nach Lehrkraft. */
  timetableColorMode: enumOf(['subject', 'teacher'], 'subject'),
});

export type SubstitutionSettings = z.infer<typeof substitutionSettingsSchema>;

/** Planungs-Engine (`SolverStore`). */
export const solverSettingsSchema = z.object({
  /** Werte aus `SolverEngine`. */
  engine: enumOf(['ASC', 'MATHPLAN', 'OPENT8', 'TIMEFOLD'], 'TIMEFOLD'),
  /** Werte aus `SolverSpeed`. */
  speed: enumOf(['fast', 'balanced', 'thorough'], 'balanced'),
});

export type SolverSettings = z.infer<typeof solverSettingsSchema>;

/**
 * Wert eines Feldes, wie er in der Profil-Uebersicht angezeigt wird. `format`
 * bekommt den gespeicherten Wert und gibt einen i18n-Key zurueck — bewusst
 * **statische** Keys, damit `i18next-parser` sie findet.
 */
export type SettingsDisplayField<S> = {
  key: keyof S & string;
  labelKey: string;
  format: (value: S[keyof S & string]) => string;
};

const booleanDisplay = (onKey: string, offKey: string) => (value: unknown) => (value ? onKey : offKey);

const mapDisplay =
  (map: Record<string, string>, fallbackKey: string) =>
  (value: unknown): string =>
    (typeof value === 'string' ? map[value] : undefined) ?? fallbackKey;

const boardLayoutLabels = {
  stacked: 'userSettings.boardLayout.stacked',
  split: 'userSettings.boardLayout.split',
};

const contextLabels = {
  classes: 'userSettings.context.classes',
  teachers: 'userSettings.context.teachers',
  rooms: 'userSettings.context.rooms',
  subjects: 'userSettings.context.subjects',
};

const colorModeLabels = {
  subject: 'userSettings.colorMode.subject',
  teacher: 'userSettings.colorMode.teacher',
};

const shortNameLabels = {
  subject: 'userSettings.shortName.subject',
  teacher: 'userSettings.shortName.teacher',
};

const engineLabels = {
  ASC: 'userSettings.engine.asc',
  MATHPLAN: 'userSettings.engine.mathplan',
  OPENT8: 'userSettings.engine.opent8',
  TIMEFOLD: 'userSettings.engine.timefold',
};

const speedLabels = {
  fast: 'userSettings.speed.fast',
  balanced: 'userSettings.speed.balanced',
  thorough: 'userSettings.speed.thorough',
};

/**
 * Registry aller Scopes: Schema, Defaults und die Felder, die in der
 * Profil-Uebersicht angezeigt werden.
 *
 * `displayFields` listet bewusst nur Werte, die ohne Vorwissen verstaendlich
 * sind. Pixel- und Prozentwerte (Trennlinien, Zeilen- und Zellhoehen) sowie
 * Highlight-Modi tauchen nicht auf, werden beim Zuruecksetzen aber mitgeloescht.
 */
export const SETTINGS_REGISTRY = {
  pinboard: {
    version: 1,
    schema: pinboardSettingsSchema,
    labelKey: 'userSettings.scope.pinboard',
    displayFields: [
      {
        key: 'boardLayout',
        labelKey: 'userSettings.field.boardLayout',
        format: mapDisplay(boardLayoutLabels, 'common.unknown'),
      },
      {
        key: 'cardColorBySubject',
        labelKey: 'userSettings.field.cardColor',
        format: booleanDisplay(colorModeLabels.subject, colorModeLabels.teacher),
      },
      {
        key: 'showTeacherInsteadOfSubject',
        labelKey: 'userSettings.field.cardShortName',
        format: booleanDisplay(shortNameLabels.teacher, shortNameLabels.subject),
      },
      {
        key: 'context',
        labelKey: 'userSettings.field.context',
        format: mapDisplay(contextLabels, 'userSettings.default'),
      },
      {
        key: 'showPreviewMap',
        labelKey: 'userSettings.field.previewMap',
        format: booleanDisplay('common.on', 'common.off'),
      },
    ] as SettingsDisplayField<PinboardSettings>[],
  },
  teachingBlock: {
    version: 1,
    schema: teachingBlockSettingsSchema,
    labelKey: 'userSettings.scope.teachingBlock',
    displayFields: [
      {
        key: 'boardLayout',
        labelKey: 'userSettings.field.boardLayout',
        format: mapDisplay(boardLayoutLabels, 'common.unknown'),
      },
      {
        key: 'cardColorBySubject',
        labelKey: 'userSettings.field.cardColor',
        format: booleanDisplay(colorModeLabels.subject, colorModeLabels.teacher),
      },
      {
        key: 'showTeacherInsteadOfSubject',
        labelKey: 'userSettings.field.cardShortName',
        format: booleanDisplay(shortNameLabels.teacher, shortNameLabels.subject),
      },
      {
        key: 'gridContext',
        labelKey: 'userSettings.field.context',
        format: mapDisplay(contextLabels, 'userSettings.default'),
      },
      {
        key: 'showPreviewMap',
        labelKey: 'userSettings.field.previewMap',
        format: booleanDisplay('common.on', 'common.off'),
      },
    ] as SettingsDisplayField<TeachingBlockSettings>[],
  },
  substitutions: {
    version: 1,
    schema: substitutionSettingsSchema,
    labelKey: 'userSettings.scope.substitutions',
    displayFields: [
      {
        key: 'timetableColorMode',
        labelKey: 'userSettings.field.timetableColorMode',
        format: mapDisplay(colorModeLabels, 'common.unknown'),
      },
    ] as SettingsDisplayField<SubstitutionSettings>[],
  },
  solver: {
    version: 1,
    schema: solverSettingsSchema,
    labelKey: 'userSettings.scope.solver',
    displayFields: [
      { key: 'engine', labelKey: 'userSettings.field.engine', format: mapDisplay(engineLabels, 'common.unknown') },
      { key: 'speed', labelKey: 'userSettings.field.speed', format: mapDisplay(speedLabels, 'common.unknown') },
    ] as SettingsDisplayField<SolverSettings>[],
  },
} as const;

export type SettingsByScope = {
  pinboard: PinboardSettings;
  teachingBlock: TeachingBlockSettings;
  substitutions: SubstitutionSettings;
  solver: SolverSettings;
};

/** Defaults eines Scopes — das Ergebnis des Schemas auf einem leeren Objekt. */
export function defaultSettings<S extends SettingsScope>(scope: S): SettingsByScope[S] {
  return SETTINGS_REGISTRY[scope].schema.parse({}) as SettingsByScope[S];
}

/**
 * Parst den gespeicherten JSON-String. Kaputtes JSON oder ein falscher Typ
 * ergeben die Defaults; einzelne unbrauchbare Felder fangen die `.catch()` im
 * Schema ab.
 */
export function parseSettings<S extends SettingsScope>(scope: S, data: string | null | undefined): SettingsByScope[S] {
  if (!data) return defaultSettings(scope);

  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return defaultSettings(scope);
  }

  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return defaultSettings(scope);
  }

  const result = SETTINGS_REGISTRY[scope].schema.safeParse(raw);
  return (result.success ? result.data : defaultSettings(scope)) as SettingsByScope[S];
}
